import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, MapPin, Building, Home, Key, TrendingUp, RefreshCw, Bot, ChevronRight, Database, ShieldCheck, Layers, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MapBoundsUpdater = ({ properties }) => {
  const map = useMap();
  useEffect(() => {
    if (!properties || properties.length === 0) return;
    const coords = properties
      .filter(p => p.latitude && p.longitude)
      .map(p => [p.latitude, p.longitude]);
    if (coords.length > 0) {
      map.fitBounds(L.latLngBounds(coords), { padding: [50, 50] });
    }
  }, [properties, map]);
  return null;
};

const NationalDealSearch = () => {
  const navigate = useNavigate();
  const [searchMode, setSearchMode] = useState('land');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);

  const performSearch = async () => {
    if (!city || !state) {
      toast.error("Workflow Parameters Required (City + ST)");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/national-search/search`, {
        cities: [city],
        states: [state.toUpperCase()],
        property_types: searchMode === 'land' ? ['Land'] : ['Single Family', 'Multi-Family'],
        limit: 80
      });
      
      if (res.data.properties && res.data.properties.length > 0) {
        setProperties(res.data.properties);
        toast.success(`Satellite Link Established: ${res.data.properties.length} Assets Identified`);
      } else {
        setProperties([]);
        toast.info("Area scan complete. Zero high-confidence matches.");
      }
    } catch (err) {
      toast.error("Network Failure: Logic Engine Offline");
    } finally {
      setLoading(false);
    }
  };

  const handleUnderwrite = (prop) => {
    navigate('/underwriting', { state: { property: { ...prop, bot_type: searchMode } } });
  };

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden bg-white page-transition">
      {/* Sidebar Controls */}
      <div className="w-[450px] flex flex-col border-r border-slate-100 p-8 space-y-8 bg-white z-10 overflow-y-auto shadow-2xl">
        <header className="space-y-1">
          <Badge variant="outline" className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">Phase 1: Market Intel</Badge>
          <h1 className="text-2xl font-black tracking-tighter uppercase italic text-slate-900">Market Intelligence</h1>
          <p className="text-xs text-slate-400 font-medium">Extract on/off-market deals from national registries.</p>
        </header>

        <Card className="border-2 border-slate-100 shadow-sm rounded-2xl overflow-hidden bg-slate-50/50">
          <Tabs value={searchMode} onValueChange={setSearchMode} className="w-full">
            <TabsList className="grid grid-cols-4 h-12 bg-slate-100/50 gap-1 p-1 border-b border-slate-100">
              <TabsTrigger value="land" className="text-[9px] font-black uppercase italic">Land</TabsTrigger>
              <TabsTrigger value="wholesale" className="text-[9px] font-black uppercase italic">SFR</TabsTrigger>
              <TabsTrigger value="section8" className="text-[9px] font-black uppercase italic">S8</TabsTrigger>
              <TabsTrigger value="multifamily" className="text-[9px] font-black uppercase italic">MFH</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="p-6 space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Geography</label>
                <div className="flex gap-2">
                   <Input placeholder="CITY (E.G. AUSTIN)" value={city} onChange={e => setCity(e.target.value)} className="h-12 text-sm font-black bg-white border-slate-200 uppercase" />
                   <Input placeholder="ST" value={state} onChange={e => setState(e.target.value)} className="h-12 w-20 text-sm font-black bg-white border-slate-200 text-center uppercase" maxLength={2} />
                </div>
              </div>
              
              <Button onClick={performSearch} disabled={loading} className="w-full h-14 text-sm font-black bg-slate-900 hover:bg-slate-800 transition-all active:scale-95 shadow-xl shadow-slate-900/10">
                {loading ? <RefreshCw className="animate-spin mr-2" size={16}/> : <Database className="mr-2" size={16}/>}
                EXECUTE SEARCH
              </Button>
            </div>
          </div>
        </Card>

        {/* Results List */}
        <div className="flex-1 space-y-4">
           <div className="flex items-center justify-between px-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{properties.length} Assets Identified</span>
              <Filter size={14} className="text-slate-300" />
           </div>
           
           <div className="space-y-3">
             {properties.map((prop, idx) => (
                <div key={idx} onClick={() => handleUnderwrite(prop)} className="group p-5 border-2 border-slate-50 hover:border-slate-900 bg-white rounded-2xl transition-all cursor-pointer shadow-sm hover:shadow-xl">
                   <div className="flex justify-between items-start mb-3">
                      <div>
                         <p className="text-[10px] font-black text-slate-400 uppercase truncate max-w-[200px]">{prop.formattedAddress || prop.address}</p>
                         <p className="text-xl font-black italic tracking-tighter text-slate-900">${prop.price?.toLocaleString()}</p>
                      </div>
                      <Badge className={prop.is_off_market ? 'bg-emerald-500' : 'bg-slate-900'}>
                         {prop.is_off_market ? 'OFF-MARKET' : 'LISTED'}
                      </Badge>
                   </div>
                   <div className="flex items-center justify-between text-[10px] font-black uppercase text-slate-400">
                      <span>{prop.bedrooms}B / {prop.bathrooms}B</span>
                      <span className="flex items-center gap-1 text-emerald-500">
                         <ShieldCheck size={10} /> 94% Intel
                      </span>
                   </div>
                </div>
             ))}
             {properties.length === 0 && (
                <div className="text-center py-20 opacity-20 italic text-sm font-bold">Scanning airwaves...</div>
             )}
           </div>
        </div>
      </div>

      {/* Map Main View */}
      <div className="flex-1 relative bg-slate-50">
        <MapContainer center={[27.9506, -82.4572]} zoom={4} style={{ height: '100%', width: '100%' }} className="z-0">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapBoundsUpdater properties={properties} />
          {properties.map((prop, idx) => (
            <Marker 
              key={idx} 
              position={[prop.latitude || 0, prop.longitude || 0]} 
              eventHandlers={{ click: () => handleUnderwrite(prop) }}
            >
              <Popup className="rounded-2xl overflow-hidden">
                <div className="p-2 space-y-2">
                  <p className="font-black text-xs uppercase italic">{prop.formattedAddress || prop.address}</p>
                  <p className="text-lg font-black text-slate-900">${prop.price?.toLocaleString()}</p>
                  <Button size="sm" className="w-full bg-slate-900 text-[10px] font-bold h-8">UNDERWRITE DEAL</Button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
        
        {/* Live Status Overlay */}
        <div className="absolute top-6 left-6 z-[1000]">
           <div className="flex items-center gap-3 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full border-2 border-slate-100 shadow-xl">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-900">National Node: Online</span>
           </div>
        </div>
      </div>
    </div>
  );
};

export default NationalDealSearch;
